import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import LoginView from "@/views/LoginView.vue";
import NavView from "@/views/NavView.vue";
import DashboardView from "@/views/DashboardView.vue";
import DeviceManagement from "@/views/DeviceManagement.vue";

import CallRecords from "@/views/CallRecords.vue";
import OTAManagement from "@/views/OTAManagement.vue";
import DeviceDetails from "@/views/DeviceDetails.vue"; // 1. 导入新组件
import SimCardStats from "@/views/SimCardStats.vue"; // SIM卡统计页面
import SimCardList from "@/views/SimCardList.vue"; // SIM卡列表页面
import RecordingManagement from "@/views/RecordingManagement.vue"; // 录音管理页面
import RTCStats from "@/views/RTCStats.vue"; // RTC统计页面
import PackageManagement from "@/views/PackageManagement.vue"; // 套餐管理页面
import TenantManagement from "@/views/TenantManagement.vue";
import TenantApiKeys from "@/views/TenantApiKeys.vue";
import { ElMessage } from "element-plus";
import jsCookie from "js-cookie";
import apiClient from "@/services/apiClient";
import config, { API_URLS } from "@/config";
import store from "@/store";
import { ROUTE_GUARD_PERMISSIONS } from "@/types/permission";
import UserProfile from "@/views/UserProfile.vue";
import UsersManagement from "@/views/UsersManagement.vue";

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    redirect: "/dashboard",
  },
  {
    path: "/login",
    name: "用户登录",
    component: LoginView,
    meta: { show: false },
  },
  {
    path: "/nav",
    name: "导航",
    component: NavView,
    redirect: "/dashboard",
    meta: { show: true },
    children: [
      {
        path: "/dashboard",
        name: "实时监控",
        component: DashboardView,
        meta: { show: true },
      },
      {
        path: "/deviceManagement",
        name: "设备管理",
        component: DeviceManagement,
        meta: { show: true },
      },

      {
        path: "/device/:id", // 2. 添加新路由规则
        name: "设备详情",
        component: DeviceDetails,
        meta: { show: false }, // 在侧边栏导航中不显示
      },
      {
        path: "/callRecords",
        name: "通话记录",
        component: CallRecords,
        meta: { show: true },
      },
      {
        path: "/call-records/:device_id",
        name: "设备通话明细",
        component: CallRecords,
        meta: { show: false },
        props: true,
      },
      {
        path: "/ota",
        name: "OTA升级",
        component: OTAManagement,
        meta: { show: true },
      },
      {
        path: "/simCardStats",
        name: "SIM卡统计",
        component: SimCardStats,
        meta: { show: true },
      },
      {
        path: "/simCardList",
        name: "SIM卡列表",
        component: SimCardList,
        meta: { show: true },
      },
      {
        path: "/recordingManagement",
        name: "录音管理",
        component: RecordingManagement,
        meta: { show: true },
      },
      {
        path: "/rtcStats",
        name: "RTC统计",
        component: RTCStats,
        meta: { show: true },
      },
      {
        path: "/packageManagement",
        name: "套餐管理",
        component: PackageManagement,
        meta: { show: true },
      },
      {
        path: "/tenantManagement",
        name: "租户管理",
        component: TenantManagement,
        meta: { show: true },
      },
      {
        path: "/tenantApiKeys",
        name: "API密钥",
        component: TenantApiKeys,
        meta: { show: true },
      },
      {
        path: "/profile",
        name: "个人资料",
        component: UserProfile,
        meta: { show: false },
      },
      {
        path: "/users",
        name: "用户管理",
        component: UsersManagement,
        meta: { show: false },
      },
      // {
      //   path: "/test",
      //   name: "WebSocket测试",
      //   component: TestView,
      //   meta: { show: false }, // 不在导航中显示
      // },
    ],
  },
];

const router = createRouter({
  history: createWebHistory("/"),
  routes,
});

// 修复：重写 validateToken 函数以使用正确的 API 端点和逻辑
const validateToken = async (token: string): Promise<boolean> => {
  try {
    // 最佳实践：前端通过调用一个受保护的"获取当前用户信息"的端点来验证token
    // 这里我们使用在 config/index.ts 中定义的 API_URLS.USER_INFO
    await apiClient.get(API_URLS.USER_INFO);
    // 如果请求成功（HTTP 2xx），说明token有效
    return true;
  } catch (error) {
    // 如果请求失败（例如401 Unauthorized），说明token无效或已过期
    console.error("Token validation failed:", error);
    return false;
  }
};

// 使用集中化的路由权限配置
const routeRoleMap = ROUTE_GUARD_PERMISSIONS;

router.beforeEach(async (to, from, next) => {
  const token = jsCookie.get("userToken");

  if (to.path === "/login") {
    // 如果已经有token，并且访问的是登录页，直接跳转到主页
    if (token) {
      next("/dashboard");
    } else {
      next();
    }
    return;
  }

  if (!token) {
    ElMessage.error("认证已过期或无效，请重新登录");
    next("/login");
    return;
  }

  try {
    const isValidToken = await validateToken(token);
    if (!isValidToken) {
      jsCookie.remove("userToken");
      jsCookie.remove("username");
      store.dispatch("logout");
      next("/login");
      return;
    }
  } catch (error) {
    console.error("Token validation failed:", error);
    jsCookie.remove("userToken");
    jsCookie.remove("username");
    store.dispatch("logout");
    next("/login");
    return;
  }

  // 权限判断：根据store中的用户角色判断是否有权访问目标路由
  const userRole = store.getters.getUserRole;
  const allowedRoles = routeRoleMap[to.path];

  if (allowedRoles && userRole && !allowedRoles.includes(userRole)) {
    ElMessage.error("无权限访问该页面");
    // 简化重定向逻辑：所有无权限的用户都统一跳转到仪表盘页面
    next("/dashboard");
    return;
  }

  next();
});

export default router;
